import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { invoicesApi } from '../../api/invoices';
import { formatMoney } from '../../utils/formatMoney';
import { formatDate, monthsAgo, today } from '../../utils/formatDate';
import DataTable from '../../components/DataTable';
import LoadingSpinner from '../../components/LoadingSpinner';
import ConfirmDialog from '../../components/ConfirmDialog';
import DatePickerInput from '../../components/DatePickerInput';
import { useReportScreen } from '../../components/ScreenContext';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useIsMirror } from '../../hooks/useIsMirror';

interface Props {
  direction: 'Made' | 'Received';
}

export default function InvoiceList({ direction }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // Initialize date filters from URL params or defaults.
  // AI copilot can set these via navigation: /invoices/made?from=2024-01-01&to=2026-12-31
  const urlFrom = searchParams.get('from');
  const urlTo = searchParams.get('to');
  const [from, setFrom] = useState(urlFrom ?? monthsAgo(3));
  const [to, setTo] = useState(urlTo ?? today());
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; direction: string } | null>(null);
  const [postingTarget, setPostingTarget] = useState<{ id: number; direction: string } | null>(null);
  // ADR-004: mirror workspaces (mysql, asmx-owned) are READ-ONLY — hide the
  // write affordances; the API 403s them anyway (defense in depth).
  const isMirror = useIsMirror();

  // Sync URL search params to state when they change (e.g., AI-driven navigation)
  useEffect(() => {
    const newFrom = searchParams.get('from');
    const newTo = searchParams.get('to');
    const newQ = searchParams.get('q');
    if (newFrom) setFrom(newFrom);
    if (newTo) setTo(newTo);
    if (newQ) setSearch(newQ);
  }, [searchParams]);

  const { data: invoices, isLoading } = useQuery({
    queryKey: ['invoices', direction, from, to, debouncedSearch],
    queryFn: () => invoicesApi.list({ direction, from, to, q: debouncedSearch || undefined, limit: 200 }),
  });

  const deleteMutation = useMutation({
    mutationFn: (params: { id: number; direction: string }) =>
      params.direction === 'Made'
        ? invoicesApi.deleteMade(params.id)
        : invoicesApi.deleteReceived(params.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setDeleteTarget(null);
    },
  });

  const postMutation = useMutation({
    mutationFn: (params: { id: number; direction: string }) =>
      invoicesApi.post(params.id, params.direction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setPostingTarget(null);
    },
  });

  // Screen context for AI copilot — must be above any early return (Rules of Hooks)
  const invoiceCount = invoices?.length ?? 0;
  const screenSummary = direction === 'Made'
    ? `Pardavimu saskaitos (${invoiceCount} irasu)`
    : `Pirkimu saskaitos (${invoiceCount} irasu)`;

  const screenFilters = useMemo(() => {
    const f: Record<string, string> = { nuo: from, iki: to };
    if (search) f.paieska = search;
    return f;
  }, [from, to, search]);

  const screenPreview = useMemo(() =>
    (invoices ?? []).slice(0, 5).map(inv =>
      `${inv.documentNumber || inv.id}: ${formatMoney(inv.totalSum)} EUR (${formatDate(inv.date)})`
    ), [invoices]);

  useReportScreen(screenSummary, {
    recordCount: invoiceCount,
    filters: screenFilters,
    preview: screenPreview,
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      {/* Header. The tab strip lives in InvoicesLayout (real tabs - it stays on
          screen across the four surfaces). The New button is ALWAYS visible;
          read-only (mirror) workspaces disable it instead of hiding it. */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">
          {direction === 'Made' ? t('invoices.salesTitle') : t('invoices.purchaseTitle')}
        </h1>
        <button
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={() => navigate('/invoices/new')}
          disabled={isMirror}
          title={isMirror ? t('invoices.readOnlyHint') : undefined}
        >
          {t('invoices.newInvoice')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <DatePickerInput
          label={t('common.from')}
          value={from}
          onChange={setFrom}
          inputClassName="w-36"
        />
        <DatePickerInput
          label={t('common.to')}
          value={to}
          onChange={setTo}
          inputClassName="w-36"
        />
        <div className="flex-1 min-w-48">
          <label className="block text-xs text-gray-500 mb-1">{t('common.search')}</label>
          <input
            type="text"
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
            placeholder={t('common.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Invoice table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <DataTable
          columns={[
            {
              key: 'status',
              header: '',
              className: 'w-8 text-center',
              render: (row) => !row.isPosted ? (
                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-700">
                  {t('invoices.draft')}
                </span>
              ) : null,
            },
            { key: 'date', header: t('common.date'), render: (row) => formatDate(row.date), className: 'whitespace-nowrap' },
            { key: 'documentNumber', header: t('common.docNumber'), className: 'whitespace-nowrap' },
            { key: 'personName', header: direction === 'Made' ? t('invoices.client') : t('invoices.supplier'), render: (row) => row.personName ?? `ID: ${row.personId}` },
            { key: 'content', header: t('common.content'), className: 'max-w-48 truncate' },
            { key: 'totalSum', header: t('invoices.sum'), render: (row) => <span className="num">{formatMoney(row.totalSum)}</span>, className: 'text-right' },
            { key: 'totalVat', header: t('invoices.vat'), render: (row) => <span className="num">{formatMoney(row.totalVat)}</span>, className: 'text-right' },
            { key: 'currencyCode', header: t('common.currency'), className: 'text-center' },
            {
              key: 'actions', header: '', className: 'text-right w-32',
              render: (row) => (
                <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                  {!isMirror && !row.isPosted && (
                    <button
                      className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                      onClick={() => setPostingTarget({ id: row.id, direction: row.direction })}
                    >
                      {t('invoices.post')}
                    </button>
                  )}
                  {!isMirror && (
                    <button
                      className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                      onClick={() => setDeleteTarget({ id: row.id, direction: row.direction })}
                    >
                      {t('common.delete').substring(0, 3)}
                    </button>
                  )}
                </div>
              ),
            },
          ]}
          data={invoices ?? []}
          keyFn={(row) => `${row.direction}-${row.id}`}
          rowClassName={(row) => !row.isPosted ? 'bg-amber-50' : ''}
          onRowClick={(row) => navigate(`/invoices/${row.id}?direction=${row.direction}`)}
          emptyMessage={t('invoices.noInvoicesFound')}
        />
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title={t('invoices.deleteTitle')}
        message={t('invoices.deleteMessage', { id: deleteTarget?.id })}
        confirmLabel={t('common.delete')}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Post confirmation */}
      <ConfirmDialog
        open={!!postingTarget}
        title={t('invoices.postTitle')}
        message={t('invoices.postMessage', { id: postingTarget?.id })}
        confirmLabel={t('invoices.post')}
        onConfirm={() => postingTarget && postMutation.mutate(postingTarget)}
        onCancel={() => setPostingTarget(null)}
      />
    </div>
  );
}
